<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useProjeto, useBasicosProjetos } from '@/modules/projeto/composables/useProjeto';
import { detalhamentorubrica_indefinida } from '@/commons/rubricas';

const { rubricas, carregarRubricas, rubricasDetalhes, carregarRubricasDetalhes } = useProjeto();
const { listaTiposSolicitacoesProjetos, carregarTiposSolicitacoesProjetos } = useBasicosProjetos();

const props = defineProps({
    projetoId: String,
    rubricaId: {
        type: Number,
        default: null
    },
    modelValue: {
        type: Object,
        default: null
    },
    tiposSolicitacoes: {
        type: Array,
        required: true
    },
    tipoSolicitacaoId: {
        type: Number,
        default: null
    },
    rubricaDetalheId: {
        type: Number,
        default: null
    },
    error: {
        type: String,
        default: null
    },
    label: {
        type: String,
        default: 'Rubrica'
    },
    required: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'change', 'temDetalhamento']);

const rubricaSelecionada = ref(null);
const jaCarregouDadosEdicao = ref(false);
const loading = ref(false);

// Observar mudanças no modelValue para sincronizar (útil na edição)
watch(() => props.modelValue, (newValue) => {
    if (newValue && newValue !== rubricaSelecionada.value) {
        rubricaSelecionada.value = newValue;
    }
});

// Observar mudanças no tipoSolicitacaoId para carregar dados de edição
watch(() => props.tipoSolicitacaoId, async (newValue) => {
    if (newValue && !jaCarregouDadosEdicao.value && rubricas.value.length > 0 && listaTiposSolicitacoesProjetos.value.length > 0) {
        loading.value = true;
        await carregarDadosEdicao();
        jaCarregouDadosEdicao.value = true;
        loading.value = false;
    }
});

const options = computed(() => { return rubricas.value.filter(rubrica => props.tiposSolicitacoes.includes(rubrica.rubricafinanceira_codigo)); });

const consultarDetalhamento = async (rubricaCodigo) => {
    if (!rubricaCodigo) return null;

    loading.value = true;
    try {
        // Buscar tipo de solicitação comparando palavras-chave
        const palavrasChave = rubricaCodigo.rubricafinanceira_nome.toLowerCase().split(' ').filter(p => p.length > 2);

        const tipoSolicitacao = listaTiposSolicitacoesProjetos.value.find(tipo => {
            const nomeTipo = tipo.nome.toLowerCase();
            // Verifica se pelo menos 2 palavras-chave estão presentes no nome do tipo
            const palavrasEncontradas = palavrasChave.filter(palavra => nomeTipo.includes(palavra));
            return palavrasEncontradas.length >= Math.min(2, palavrasChave.length);
        });

        await carregarRubricasDetalhes(props.projetoId, rubricaCodigo.rubricafinanceira_id);

        if (rubricasDetalhes.value.detalhes.length > 1) {
            emit('update:modelValue', tipoSolicitacao);
            emit('change', tipoSolicitacao);
            const rubricasFiltradas = rubricasDetalhes.value.detalhes.filter(
                detalhe => !detalhe.descricao?.includes(detalhamentorubrica_indefinida)
            );

            emit('temDetalhamento', { exibe: true, detalhamentos: rubricasFiltradas });
        } else {
            emit('update:modelValue', tipoSolicitacao);
            emit('change', tipoSolicitacao);
            emit('temDetalhamento', { exibe: false, detalhe_id: rubricasDetalhes.value.detalhes[0].id || null });
        }
    } finally {
        loading.value = false;
    }
};

const carregarDadosEdicao = async () => {
    if (!props.tipoSolicitacaoId) return;

    // Buscar o tipo de solicitação pelo ID
    const tipoSolicitacao = listaTiposSolicitacoesProjetos.value.find(
        tipo => tipo.id === props.tipoSolicitacaoId
    );

    // Buscar a rubrica correspondente
    const palavrasChave = tipoSolicitacao.nome.toLowerCase().split(' ').filter(p => p.length > 2);

    const rubricaCorrespondente = rubricas.value.find(rubrica => {
        const nomeRubrica = rubrica.rubricafinanceira_nome.toLowerCase();
        const palavrasEncontradas = palavrasChave.filter(palavra => nomeRubrica.includes(palavra));
        return palavrasEncontradas.length >= Math.min(2, palavrasChave.length);
    });

    rubricaSelecionada.value = rubricaCorrespondente;

    // Carregar os detalhamentos
    await carregarRubricasDetalhes(props.projetoId, rubricaCorrespondente.rubricafinanceira_id);

    if (rubricasDetalhes.value.detalhes && rubricasDetalhes.value.detalhes.length > 1) {
        const rubricasFiltradas = rubricasDetalhes.value.detalhes.filter(
            detalhe => !detalhe.descricao?.includes(detalhamentorubrica_indefinida)
        );

        emit('temDetalhamento', { exibe: true, detalhamentos: rubricasFiltradas });
    } else if (rubricasDetalhes.value.detalhes && rubricasDetalhes.value.detalhes.length > 0) {
        emit('temDetalhamento', { exibe: false, detalhe_id: rubricasDetalhes.value.detalhes[0].id || null });
    }

    emit('update:modelValue', tipoSolicitacao);
    emit('change', tipoSolicitacao);

};

onMounted(async () => {
    loading.value = true;

    try {
        await carregarRubricas(props.projetoId);
        await carregarTiposSolicitacoesProjetos();

        if (props.tipoSolicitacaoId && rubricas.value.length > 0 && listaTiposSolicitacoesProjetos.value.length > 0) {
            await carregarDadosEdicao();
            jaCarregouDadosEdicao.value = true;
        }
    } finally {
        loading.value = false;
    }
});

</script>

<template>
    <div class="field mb-2 col-12">
        <label for="rubrica_selecao" class="font-medium text-900 text-sm"> {{ label }}
            <span v-if="required" class="text-red-400">*</span>
        </label>

        <Dropdown :inputId="'rubrica_selecao'" v-model="rubricaSelecionada" :options="options"
            @change="consultarDetalhamento(rubricaSelecionada)" optionLabel="rubricafinanceira_nome"
            emptyFilterMessage="Nenhum item encontrado" :placeholder="'Selecione a rubrica'" class="text-sm"
            :class="{ 'p-invalid': error }" :loading="loading" :disabled="loading" :pt="{
                emptyFilterMessage: { class: 'text-sm' },
            }">
            <template #value="slotProps">
                <div v-if="slotProps.value" class="flex align-items-center">
                    <div class="text-sm">{{ slotProps.value.rubricafinanceira_nome }}</div>
                </div>
                <span v-else>
                    <div class="text-sm">
                        {{ slotProps.placeholder }}
                    </div>
                </span>
            </template>
            <template #option="slotProps">
                <div class="flex flex-column">
                    <p class="m-0 p-0 font-semibold text-sm">
                        {{ slotProps.option.rubricafinanceira_nome }}
                    </p>
                    <span class="text-xs">{{ slotProps.option.rubricafinanceira_codigo }}</span>
                </div>
            </template>
        </Dropdown>
        <SmallError :error="error" />
    </div>
</template>
